import {
  useOrganizationsServiceOrganizationsAccessList,
  useUsersServiceUsersInviteCreate
} from '@organization/api/queries'
import {
  ApplicationTeamPermissions,
  TeamList,
  TeamModify
} from '@organization/api/requests'
import { inviteUrl } from '@organization/utils'
import {
  PermissionsCountBadge,
  PermissionsTabLabel,
  PermissionsTotalText
} from '@shared/components'
import { useSessionContext } from '@shared/hooks'
import {
  flattenScopedPermissions,
  getPermissionsSummary,
  showApiErrors
} from '@shared/utils'
import { App, Button, Flex, Form, Input, Modal, Tabs } from 'antd'
import Select, { DefaultOptionType } from 'antd/es/select'
import { ArrowDown } from 'iconsax-react'

import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  ApplicationPermissionsPicker,
  useApplicationPermissionSections
} from '../ApplicationPermissions'
import { OptionNotFound } from '../OptionNotFound'
import { TeamSelect } from './TeamSelect'

type Props = {
  title: string
  team?: TeamList | null
  okText: string
  hideTitle?: boolean
  isLoading?: boolean
  parent?: number | null
  onSubmit: (payload: Partial<TeamModify>) => void
  onCancel: () => void
}

const DETAILS_TAB = 'details'
const PERMISSIONS_TAB = 'permissions'

export const TeamForm = ({
  title,
  okText,
  team,
  hideTitle,
  isLoading,
  parent,
  onSubmit,
  onCancel
}: Props) => {
  const [form] = Form.useForm()
  const { notification } = App.useApp()
  const { user } = useSessionContext()
  const { t } = useTranslation()

  const [searchValue, setSearchValue] = useState('')
  const [activeTab, setActiveTab] = useState(DETAILS_TAB)

  const organizationId = Number(user?.organization?.id)

  const { sections, isLoading: isCatalogLoading } =
    useApplicationPermissionSections()

  const permissions = Form.useWatch('application_permissions', form) as
    | ApplicationTeamPermissions[]
    | undefined

  const summary = useMemo(
    () =>
      getPermissionsSummary(sections, flattenScopedPermissions(permissions)),
    [sections, permissions]
  )

  const { data } = useOrganizationsServiceOrganizationsAccessList(
    {
      organization: organizationId,
      search: searchValue
    },
    [organizationId, searchValue],
    {
      enabled: !!user?.organization?.id
    }
  )

  const { mutate: inviteUser, isPending: isInviting } =
    useUsersServiceUsersInviteCreate({
      onSuccess: () => {
        notification.success({
          message: t('users.email_sent')
        })
        onCancel()
      },
      onError: (e) => showApiErrors(e, form)
    })

  const organizationUserList = data?.results?.map((user) => ({
    value: user?.user?.id,
    label: user?.user?.email
  }))

  const onInvite = () => {
    if (!organizationId || !searchValue) return

    inviteUser({
      requestBody: {
        organization_id: Number(organizationId),
        emails: [searchValue],
        invite_url: inviteUrl
      }
    })
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      const normalizedUsers = values?.users
        ?.map((user: DefaultOptionType) => {
          if (typeof user === 'number') {
            return { id: user, roles: values.roles }
          }

          if (user?.value) {
            return { id: user?.value, roles: values.roles }
          }

          return null
        })
        .filter(
          (u: DefaultOptionType): u is { id: string | number } => u !== null
        )

      const payload: Partial<TeamModify> = {
        organization: Number(organizationId),
        name: values.name,
        ...((!team || hideTitle) && {
          new_users: normalizedUsers
        }),
        parent: parent || values.parent,
        application_permissions: values?.application_permissions
      }

      onSubmit(payload)

      notification.success({
        message: t('users.team_updated')
      })

      onCancel()
    } catch (err) {
      // Câmpurile obligatorii stau pe tab-ul de detalii, deci comutăm acolo ca eroarea
      // să fie vizibilă când utilizatorul salvează din tab-ul de permisiuni.
      setActiveTab(DETAILS_TAB)

      showApiErrors(err)
    }
  }

  useEffect(() => {
    if (team) {
      form.setFieldsValue({
        name: team.name,
        application_permissions: team.application_permissions
      })
    }

    if (parent) {
      form.setFieldValue('parent', parent)
    }
  }, [team, parent, form])

  return (
    <Modal
      title={
        <Flex gap={12} align="center" wrap>
          {title}

          <PermissionsCountBadge
            active={summary.active}
            total={summary.total}
          />
        </Flex>
      }
      open
      width={900}
      destroyOnHidden
      onCancel={onCancel}
      footer={
        <Flex
          align="center"
          justify="space-between"
          gap={12}
          wrap
          className="w-full"
        >
          <PermissionsTotalText active={summary.active} />

          <Flex gap={8}>
            <Button onClick={onCancel}>{t('button.cancel')}</Button>

            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading || isInviting}
              onClick={handleSubmit}
            >
              {okText}
            </Button>
          </Flex>
        </Flex>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: DETAILS_TAB,
              label: t('permissions.tab_team_details'),
              forceRender: true,
              children: (
                <>
                  {!hideTitle && (
                    <Form.Item
                      name="name"
                      label={t('form.item.team_name')}
                      rules={[
                        {
                          required: true,
                          message: t('form.message.team_name')
                        }
                      ]}
                    >
                      <Input placeholder={t('form.placeholder.team_name')} />
                    </Form.Item>
                  )}

                  {(!team || hideTitle) && (
                    <Form.Item
                      label={t('table.users')}
                      name="users"
                      rules={[{ required: true }]}
                    >
                      <Select
                        mode="multiple"
                        placeholder={t('form.placeholder.search_email_or_name')}
                        filterOption={false}
                        onSearch={setSearchValue}
                        onSelect={() => setSearchValue('')}
                        onBlur={() => setSearchValue('')}
                        onClear={() => setSearchValue('')}
                        menuItemSelectedIcon={<ArrowDown />}
                        options={organizationUserList}
                        notFoundContent={
                          <OptionNotFound
                            isLoading={isInviting}
                            keyword={searchValue}
                            onInvite={onInvite}
                          />
                        }
                      />
                    </Form.Item>
                  )}

                  {parent && (
                    <Form.Item label={t('common.team')} name="parent">
                      <TeamSelect parent={String(parent)} disabled />
                    </Form.Item>
                  )}
                </>
              )
            },
            {
              key: PERMISSIONS_TAB,
              label: <PermissionsTabLabel active={summary.active} />,
              forceRender: true,
              children: (
                <Form.Item name="application_permissions" noStyle>
                  <ApplicationPermissionsPicker
                    sections={sections}
                    isLoading={isCatalogLoading}
                  />
                </Form.Item>
              )
            }
          ]}
        />
      </Form>
    </Modal>
  )
}
